import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PostbackService } from "@gen/tracker/v1/postback_connect";

import { createClient } from "@/lib/connect";
import { postbackKeys } from "@/lib/services/postback-service";

const client = createClient(PostbackService);

const NONCE_KEY = (campaignId: string) => `vibe-tracker.test-click.${campaignId}`;

/**
 * The buyer's own test click (доска LinkReady): the campaign link opened with
 * `vt_test=<nonce>`, a nonce this browser made. A test postback is only ever
 * sent on THAT click, and only by a button — never on a visitor's click, never
 * on its own.
 */
export function readNonce(campaignId: string): string {
  try {
    return window.sessionStorage.getItem(NONCE_KEY(campaignId)) ?? "";
  } catch {
    return "";
  }
}

export function newNonce(campaignId: string): string {
  const bytes = new Uint8Array(12);
  crypto.getRandomValues(bytes);
  const nonce = Array.from(bytes, (b) => b.toString(36).padStart(2, "0"))
    .join("")
    .replace(/[^a-z0-9]/g, "")
    .slice(0, 24);
  try {
    window.sessionStorage.setItem(NONCE_KEY(campaignId), nonce);
  } catch {
    /* the nonce lives as long as the page */
  }
  return nonce;
}

/** The link with the test marker. Pure. */
export function testLink(link: string, nonce: string): string {
  if (!link || !nonce) return "";
  return `${link}${link.includes("?") ? "&" : "?"}vt_test=${nonce}`;
}

/** Whether the test click has arrived, polled while the buyer waits for it. */
export function useTestClick(campaignId: string, nonce: string) {
  return useQuery({
    queryKey: ["test-click", campaignId, nonce] as const,
    enabled: !!campaignId && !!nonce,
    refetchInterval: (q) => (q.state.data?.found ? false : 3000),
    queryFn: async () => client.findTestClick({ campaignId, nonce }),
  });
}

export function useSendTestPostback() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { campaignId: string; networkId: string; nonce: string }) => client.sendTestPostback(v),
    onSettled: () => void qc.invalidateQueries({ queryKey: postbackKeys.all }),
  });
}

/**
 * What this campaign's lead rulings were handed on as (решение 07): the
 * newest forwards with the event vibe-dispatch makes of each, its mode
 * (live | test) and delivery state. Empty = no ruling on a lead yet.
 */
export function useSourceEvents(campaignId: string) {
  return useQuery({
    queryKey: ["source-events", campaignId] as const,
    enabled: !!campaignId,
    refetchInterval: 30_000,
    queryFn: async () => (await client.listSourceEvents({ campaignId, limit: 10 })).items,
  });
}
